import { rem } from 'polished'
import React, { useMemo } from 'react'
import styled from 'styled-components'

import ClickableLink from 'components/Common/Clickable/ClickableLink'
import Image from 'components/Common/Image'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'components/Luxkit/Button/TextButton'
import Tooltip from 'components/Luxkit/Tooltip'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { DEFAULT_TRIP_IMAGE } from 'tripPlanner/config'
import CreatorProfile from 'tripPlanner/Tripkit/CuratedTrips/CreatorProfile'
import TripLabel from 'tripPlanner/Tripkit/CuratedTrips/TripLabel'
import { BasicTrip } from 'tripPlanner/types/common'
import { getTripDescriptors } from 'tripPlanner/utils/curated/curatedTrips'

const Tile = styled(Group)`
  position: relative;
  width: 100%;
  height: 100%;

  ${mediaQueryUp.tablet} {
    max-width: ${rem(366)};
  }
`

const TileContent = styled(VerticalSpacer)`
  width: 100%;
  flex: 1;

  > * {
    text-overflow: ellipsis;
  }
`

const TripImage = styled.div`
  --label-offset: ${rem(12)};

  position: relative;
  border-radius: ${(props) => props.theme.borderRadius.S};
  overflow: hidden;
  aspect-ratio: 11 /6;
  width: 100%;

  > .image {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
  }

  > .label {
    position: absolute;
    z-index: 1;
    top: var(--label-offset);
    left: var(--label-offset);
  }
`

const DetailsCaption = styled(BodyText)`
  align-items: center;
  display: flex;

  > * + *::before {
    content: '·';
    margin: 0 ${rem(4)};
    speak-as: bullets;
    speak: none;
  }
`

const ViewTripButton = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`

const TileBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: ${rem(4)};
`

interface Props {
  trip: BasicTrip
  showDescription?: boolean
  showCTA?: boolean
}

function CuratedTripsItemTile(props: Props) {
  const { trip, showDescription, showCTA } = props

  const { metadata } = trip
  const image = useMemo((): App.Image => {
    return {
      id: trip.imageId,
      url: trip.imageId ? undefined : DEFAULT_TRIP_IMAGE,
      title: trip.name,
    }
  }, [trip])

  // const rating = 4.5

  const cityTooltip = useMemo(() => {
    if (metadata?.locations && metadata.locations.length > 0) {
      return metadata.locations
        .map((location) => location.lePlaceName?.split(',')[0])
        .filter((city) => city !== undefined)
        .join(' - ')
    }
    return undefined
  }, [metadata])

  return (
    <ClickableLink
      to={`/trip-planner/curated/${trip.id}/summary`}
      data-test-id={`trip-panel-${trip.id}`}
    >
      <Tile direction="vertical" gap={12}>
        <TripImage>
          <TripLabel className="label" variant="customisable" />
          <Image
            className="image"
            image={image}
            width={366}
            fit="center"
            dpr={2}
          />
        </TripImage>
        <TileContent gap={4}>
          <Heading variant="heading3" colour="neutral-one">
            {trip.name}
          </Heading>

          {metadata && (
            <DetailsCaption variant="medium" colour="neutral-one">
              {getTripDescriptors(metadata).map((descriptor, i) => (
                <div key={i}>{descriptor}</div>
              ))}

              {cityTooltip && (
                <CSSBreakpoint min="tablet">
                  <Tooltip placement="bottom" description={cityTooltip}>
                    Preview
                  </Tooltip>
                </CSSBreakpoint>
              )}
            </DetailsCaption>
          )}

          <TileBody>
            {trip.curator?.showBio && trip.curator.name && (
              <CreatorProfile
                size="small"
                name={trip.curator.name}
                imgId={trip.curator.imageId}
              />
            )}
            {showDescription && (
              <BodyText variant="medium" colour="neutral-two" lineClamp={2}>
                {trip.description}
              </BodyText>
            )}
          </TileBody>

          {/* <CuratedTripMetaDetails
            usedByCount={usedByCount}
            rating={rating}
            reviewCount={reviewCount}
          /> */}
        </TileContent>
        {showCTA && (
          <ViewTripButton>
            <TextButton
              kind="primary"
              variant="default"
              size="medium"
              shape="square"
              nonInteractable
            >
              View trip
            </TextButton>
          </ViewTripButton>
        )}
      </Tile>
    </ClickableLink>
  )
}

export default CuratedTripsItemTile
